/**
 * Regras de consistência tributária do cadastro de produtos (eloTributos).
 * Espelho de br.com.eloadmin.services.RegrasTributarias (backend).
 *
 * erros  = impedem a conferência; avisos = apenas alertam.
 * Cada item: { campo, mensagem } — campo = nome do campo no formulário.
 */

const digitos = v => String(v === null || v === undefined ? '' : v).replace(/\D/g, '');
const ultimos = (v, n) => { const d = digitos(v); return d.length > n ? d.slice(-n) : d; };
const pisCofins = v => { const d = digitos(v); if (!d) return ''; return d.length === 1 ? `0${d}` : d.slice(-2); };
const positivo = v => Number(v || 0) > 0;
const em = (lista, v) => lista.includes(v);

const CST_COM_ST = ['10', '30', '60', '70'];
const CST_ST_A_RECOLHER = ['10', '30', '70'];
const CST_COM_ICMS_PROPRIO = ['00', '10', '20', '70'];
const CST_COM_REDUCAO = ['20', '70'];
const CST_SEM_ICMS_PROPRIO = ['30', '40', '41', '50', '60'];
const CST_SEM_ST = ['00', '20', '40', '41', '50', '51'];

const CSOSN_COM_ST = ['201', '202', '203', '500'];
const CSOSN_ST_A_RECOLHER = ['201', '202', '203'];
const CSOSN_SEM_ST = ['101', '102', '103', '300', '400'];

const CFOP_ST_SUBSTITUIDO = ['405'];
const CFOP_ST_SUBSTITUTO = ['401', '403'];
const CFOP_SEM_ST = ['101', '102'];

const PISCOFINS_SEM_ALIQUOTA = ['04', '05', '06', '07', '08', '09'];

/** Indica se o CST/CSOSN informado utiliza campos de ST (para habilitar/destacar no formulário). */
export function usaSubstituicao(t) {
  const cst = ultimos(t.cstVenda, 2);
  const csosn = ultimos(t.csosnVenda, 3);
  return em(CST_COM_ST, cst) || cst === '90' || em(CSOSN_COM_ST, csosn) || csosn === '900';
}

export function validarRegrasTributarias(t) {
  const erros = [];
  const avisos = [];
  const erro = (campo, mensagem) => erros.push({ campo, mensagem });
  const aviso = (campo, mensagem) => avisos.push({ campo, mensagem });

  if (!t) return { erros, avisos };

  const cst = ultimos(t.cstVenda, 2);
  const csosn = ultimos(t.csosnVenda, 3);
  const cfop = ultimos(t.cfopVenda, 3);
  const cest = digitos(t.cest);
  const st = positivo(t.icmsAliquotaSt) || positivo(t.mva) || positivo(t.mvaSt) || positivo(t.icmsPRedBcSt);
  const reducao = Number(t.icmsPRedBc || 0);
  const modalidadeSt = t.icmsModalidadeBcSt === '' || t.icmsModalidadeBcSt === null || t.icmsModalidadeBcSt === undefined
    ? null : Number(t.icmsModalidadeBcSt);

  const validarCamposSt = origem => {
    if (!positivo(t.icmsAliquotaSt)) erro('icmsAliquotaSt', `${origem} exige alíquota de ICMS ST maior que zero.`);
    if (modalidadeSt === 4 && !positivo(t.mva) && !positivo(t.mvaSt)) {
      erro('mva', `${origem} com modalidade da BC ST por MVA exige MVA maior que zero.`);
    }
  };

  // CEST x ST (rejeição 806)
  const exigeCest = em(CST_COM_ST, cst) || em(CSOSN_COM_ST, csosn) || (cst === '90' && st) || (csosn === '900' && st);
  if (exigeCest && !cest) {
    const origem = cst && (em(CST_COM_ST, cst) || cst === '90') ? `CST ${cst}` : `CSOSN ${csosn}`;
    erro('cest', `CEST obrigatório para produto com substituição tributária (${origem}) - rejeição 806.`);
  }

  // ICMS - CST
  if (cst) {
    if (em(CST_COM_ICMS_PROPRIO, cst) && !positivo(t.icmsAliquota)) erro('icmsAliquota', `CST ${cst} exige alíquota de ICMS maior que zero.`);
    if (cst === '00' && reducao > 0) erro('icmsPRedBc', 'CST 00 não permite redução da base de cálculo. Use CST 20 (ou 70 com ST).');
    if (em(CST_COM_REDUCAO, cst)) {
      if (!(reducao > 0)) erro('icmsPRedBc', `CST ${cst} exige percentual de redução da base de cálculo maior que zero.`);
      else if (reducao >= 100) erro('icmsPRedBc', 'Percentual de redução da base de cálculo deve ser menor que 100%.');
    }
    if (em(CST_ST_A_RECOLHER, cst)) validarCamposSt(`CST ${cst}`);
    if (em(CST_SEM_ICMS_PROPRIO, cst)) {
      if (positivo(t.icmsAliquota)) aviso('icmsAliquota', `CST ${cst} não destaca ICMS próprio: a alíquota de ICMS informada será ignorada.`);
      if (reducao > 0) aviso('icmsPRedBc', `CST ${cst} não utiliza redução da base de cálculo do ICMS.`);
    }
    if (em(CST_SEM_ST, cst) && st) aviso('icmsAliquotaSt', `CST ${cst} não possui substituição tributária: alíquota ST / MVA serão ignoradas.`);
    if (em(['40', '41', '50', '60'], cst) && positivo(t.icmsAliquotaFcp)) aviso('icmsAliquotaFcp', `FCP informado com CST ${cst} (sem ICMS próprio): verifique.`);
  }

  // ICMS - CSOSN
  if (csosn) {
    if (em(CSOSN_ST_A_RECOLHER, csosn)) validarCamposSt(`CSOSN ${csosn}`);
    if (em(CSOSN_SEM_ST, csosn) && st && !cst) aviso('icmsAliquotaSt', `CSOSN ${csosn} não possui substituição tributária: alíquota ST / MVA serão ignoradas.`);
  }

  // CFOP x CST/CSOSN (rejeições 382/386)
  if (cfop) {
    if (em(CFOP_ST_SUBSTITUIDO, cfop)) {
      if (cst && cst !== '60') erro('cfopVenda', `CFOP ${cfop} (ST retida anteriormente) exige CST 60 - rejeição 382.`);
      if (csosn && csosn !== '500') erro('cfopVenda', `CFOP ${cfop} (ST retida anteriormente) exige CSOSN 500 - rejeição 386.`);
    } else if (em(CFOP_ST_SUBSTITUTO, cfop)) {
      if (cst && !em(['10', '70', '90'], cst)) erro('cfopVenda', `CFOP ${cfop} (contribuinte substituto) exige CST 10, 70 ou 90 - rejeição 382.`);
      if (csosn && !em(['201', '202', '203', '900'], csosn)) erro('cfopVenda', `CFOP ${cfop} (contribuinte substituto) exige CSOSN 201, 202, 203 ou 900 - rejeição 386.`);
    } else if (em(CFOP_SEM_ST, cfop)) {
      if (cst === '60' || em(CST_ST_A_RECOLHER, cst)) erro('cfopVenda', `CFOP ${cfop} (venda sem ST) não é permitido com CST ${cst}. Use CFOP 405 / 401 / 403 - rejeição 382.`);
      if (em(CSOSN_COM_ST, csosn)) erro('cfopVenda', `CFOP ${cfop} (venda sem ST) não é permitido com CSOSN ${csosn}. Use CFOP 405 / 401 / 403 - rejeição 386.`);
    }
  }

  // PIS/COFINS
  const cstSaida = pisCofins(t.pisCofinsSaidaCst);
  if (cstSaida) {
    // Alíquotas de PIS/COFINS são definidas no cliente (regime cumulativo / não cumulativo): não são validadas aqui
    if (em(PISCOFINS_SEM_ALIQUOTA, cstSaida)) {
      if (!digitos(t.naturezaReceita)) aviso('naturezaReceita', `PIS/COFINS CST ${cstSaida}: informe a natureza da receita (EFD Contribuições).`);
    }
  }

  // IBS/CBS/IS
  [['cClassTribIBSCBS', 'cClassTrib IBS/CBS'], ['cClassTribIBSCBSRegular', 'cClassTrib IBS/CBS regular'], ['cClassTribIS', 'cClassTrib IS']]
    .forEach(([campo, nome]) => {
      const v = String(t[campo] || '').trim();
      if (v && !/^\d{6}$/.test(v)) erro(campo, `${nome} deve conter 6 dígitos numéricos.`);
    });

  return { erros, avisos };
}

/** Mensagem única com os erros, para exibir em toast antes de enviar a conferência. */
export const resumoErros = erros => erros.map(e => e.mensagem).join(' • ');
